import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { ReportingTagCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as ReportingTagCategoryActions from "../../../actions/reportingTagCategories";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./ReportingTagCategoriesPage.scss";

interface ReportingTagCategoriesPageProps {
    formFilter: IFormFilter;
    reportingTagCategories: Dtos.ReportingTagCategory[];
    loadingReportingTagCategories: boolean;
    loadReportingTagCategoriesSuccess: boolean;
    loadReportingTagCategoriesFailure: boolean;
    loadReportingTagCategoriesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearReportingTagCategories: ReportingTagCategoryActions.IReportingTagCategoryClearActionCreator;
    loadReportingTagCategories: ReportingTagCategoryActions.IReportingTagCategoryLoadActionCreator;
    filter: ReportingTagCategoryActions.IReportingTagCategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const ReportingTagCategoriesPage: FC<ReportingTagCategoriesPageProps> = ({
    formFilter,
    reportingTagCategories,
    loadingReportingTagCategories,
    loadReportingTagCategoriesSuccess,
    loadReportingTagCategoriesFailure,
    loadReportingTagCategoriesErrorCode,
    permissions,
    pageSize,
    clearReportingTagCategories,
    loadReportingTagCategories,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showReportingTagCategories =
        !loadingReportingTagCategories && loadReportingTagCategoriesSuccess;


    // Load reporting tag categories
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(ReportingTagCategoriesBreadcrumbs());

            loadReportingTagCategories();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        ReportingTagCategoriesBreadcrumbs,
        loadReportingTagCategories,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadReportingTagCategoriesFailure) {
            navigate(`/error/${loadReportingTagCategoriesErrorCode}`);
        }
    }, [
        loadReportingTagCategoriesFailure,
        loadReportingTagCategoriesErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the reporting tag categories on component unmount
    useEffect(() => {
        return () => {
            clearReportingTagCategories();
        };
    }, [clearReportingTagCategories]);

    const createTable = useMemo(() => {
        if (!reportingTagCategories || reportingTagCategories.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no reporting tag categories to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = reportingTagCategories.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={reportingTagCategories}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? pageSize
                            : reportingTagCategories.length
                    }
                    pageSize={
                        showPagination
                            ? pageSize
                            : reportingTagCategories.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no reporting tag categories to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/settings/qa/reportingtagcategories/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        reportingTagCategories,
        PAGE_SIZE_OPTIONS,
        pageSize,
        DEFAULT_PAGE_SIZE,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Reporting Tag Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ReportingTagCategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/qa/reportingtagcategories/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showReportingTagCategories ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showReportingTagCategories ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showReportingTagCategories,
        createTable,
    ]);

    return (
        <QALookupsLayout
            loading={loadingReportingTagCategories}
            permission={Dtos.Permission.ReportingTagCategoriesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ReportingTagCategoriesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.reportingTagCategories.formFilter,
        reportingTagCategories: state.reportingTagCategories.formFilteredData,
        loadingReportingTagCategories:
            state.reportingTagCategories.loadState &&
            state.reportingTagCategories.loadState.status ==
                RequestState.Pending,
        loadReportingTagCategoriesSuccess:
            state.reportingTagCategories.loadState &&
            state.reportingTagCategories.loadState.status ==
                RequestState.Success,
        loadReportingTagCategoriesFailure:
            state.reportingTagCategories.loadState &&
            state.reportingTagCategories.loadState.status ==
                RequestState.Failure,
        loadReportingTagCategoriesErrorCode:
            state.reportingTagCategories.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ReportingTagCategoriesPageProps> => {
    return {
        loadReportingTagCategories: bindActionCreators(
            ReportingTagCategoryActions.LoadReportingTagCategory,
            dispatch
        ),
        clearReportingTagCategories: bindActionCreators(
            ReportingTagCategoryActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            ReportingTagCategoryActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ReportingTagCategoriesPage);
